import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatNom, formatPrenom } from '../lib/personne';
import { CiviliteSelect } from '../components/CiviliteSelect';

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    civilite: '',
    nom: '',
    prenom: '',
    role: 'candidat',
    telephone: ''
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData);
      toast.success('Compte créé avec succès');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1758270704286-83476deb3bd1?crop=entropy&cs=srgb&fm=jpg&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 to-slate-900/90" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <span className="text-3xl font-bold text-white font-['Plus_Jakarta_Sans']">OF-Manager</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 font-['Plus_Jakarta_Sans']">
            Rejoignez notre<br />plateforme
          </h1>
          <p className="text-lg text-slate-300 max-w-md">
            Créez votre compte pour accéder à toutes les fonctionnalités de gestion de formations.
          </p>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">OF-Manager</span>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Créer un compte</CardTitle>
              <CardDescription>
                Remplissez le formulaire pour vous inscrire
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-[100px_1fr_1fr] gap-4">
                  <CiviliteSelect value={formData.civilite} onChange={(v) => setFormData({ ...formData, civilite: v })} testId="register-civilite" />
                  <div className="space-y-2">
                    <Label htmlFor="prenom">Prénom</Label>
                    <Input
                      id="prenom"
                      name="prenom"
                      placeholder="Jean"
                      value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: formatPrenom(e.target.value) })}
                      required
                      data-testid="register-prenom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nom">Nom</Label>
                    <Input
                      id="nom"
                      name="nom"
                      placeholder="DUPONT"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: formatNom(e.target.value) })}
                      required
                      data-testid="register-nom"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="nom@exemple.fr"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="register-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telephone">Téléphone</Label>
                  <Input
                    id="telephone"
                    name="telephone"
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={formData.telephone}
                    onChange={handleChange}
                    data-testid="register-telephone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Rôle</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="register-role">
                      <SelectValue placeholder="Sélectionner un rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="candidat">Candidat</SelectItem>
                      <SelectItem value="formateur">Formateur</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Votre mot de passe"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    data-testid="register-password"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-indigo-600 hover:bg-indigo-700"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    'Créer mon compte'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Déjà un compte ?{' '}
                  <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium" data-testid="login-link">
                    Se connecter
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
